/**
 * Cloudflare Setup Page - Fastest Deployment (~2 minutes)
 *
 * Step-by-step guide for connecting Cloudflare account
 */

import axios from 'axios'
import { AlertCircle, Check, Cloud, ExternalLink, Loader } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8888'

interface CloudflareCredentials {
  api_token: string
  account_id: string
}

interface ResourceSelection {
  compute: boolean
  database: boolean
  storage: boolean
  ai: boolean
  cache: boolean
}

export default function CloudflareSetup() {
  const navigate = useNavigate()
  const [step, setStep] = useState(1)
  const [credentials, setCredentials] = useState<CloudflareCredentials>({
    api_token: '',
    account_id: ''
  })
  const [resources, setResources] = useState<ResourceSelection>({
    compute: true,
    database: true,
    storage: true,
    ai: true,
    cache: true
  })
  const [validating, setValidating] = useState(false)
  const [deploying, setDeploying] = useState(false)
  const [deploymentProgress, setDeploymentProgress] = useState(0)
  const [deploymentMessage, setDeploymentMessage] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [ws, setWs] = useState<WebSocket | null>(null)

  const handleConnect = async () => {
    setValidating(true)
    setError(null)

    try {
      const token = localStorage.getItem('token')
      const response = await axios.post(
        `${API_URL}/api/aicloud/user-cloud/connect`,
        {
          provider: 'cloudflare',
          credentials: credentials,
          tier: 'pro'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      )

      if (response.data.success) {
        setStep(4)
      }
    } catch (err) {
      setError((err as any).response?.data?.detail as string | null || 'Failed to connect Cloudflare account')
    } finally {
      setValidating(false)
    }
  }

  const handleDeploy = async () => {
    setDeploying(true)
    setError(null)

    try {
      const token = localStorage.getItem('token')
      const response = await axios.post(
        `${API_URL}/api/aicloud/user-cloud/deploy`,
        {
          provider: 'cloudflare',
          resources: resources
        },
        { headers: { Authorization: `Bearer ${token}` } }
      )

      if (response.data.success) {
        const deploymentId = String(response.data.deployment_id)
        const wsUrl = `wss://runai.clouplay.workers.dev/ws/deployment/${deploymentId}`
        const websocket = new WebSocket(wsUrl)

        websocket.onmessage = (event: MessageEvent) => {
          const data = JSON.parse(event.data as string) as {
            error?: string
            progress: number
            message: string
            status: string
          }

          if (data.error) {
            setError(data.error)
            setDeploying(false)
            return
          }

          setDeploymentProgress(data.progress)
          setDeploymentMessage(data.message)

          if (data.status === 'completed') {
            setDeploying(false)
            setStep(5)
          } else if (data.status === 'failed') {
            setError(data.message)
            setDeploying(false)
          }
        }

        websocket.onerror = () => {
          setError('WebSocket connection failed')
          setDeploying(false)
        }

        setWs(websocket)
      }
    } catch (err) {
      setError((err as any).response?.data?.detail as string | null || 'Failed to start deployment')
      setDeploying(false)
    }
  }

  useEffect(() => {
    return () => {
      if (ws) {
        ws.close()
      }
    }
  }, [ws])

  return (
    <div style={{ padding: '40px', maxWidth: '900px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '40px' }}>
        <h1 style={{ fontSize: '36px', marginBottom: '16px', display: 'flex', alignItems: 'center' }}>
          <Cloud size={36} style={{ marginRight: '16px' }} />
          Cloudflare Setup
        </h1>
        <p style={{ fontSize: '18px', color: 'var(--text-secondary)' }}>
          Fastest deployment - Get your AI infrastructure running in ~2 minutes
        </p>
      </div>

      {/* Progress Steps */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '40px' }}>
        {[
          { num: 1, label: 'Account' },
          { num: 2, label: 'API Token' },
          { num: 3, label: 'Credentials' },
          { num: 4, label: 'Resources' },
          { num: 5, label: 'Deploy' }
        ].map((s) => (
          <div key={s.num} style={{ flex: 1, textAlign: 'center' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: step >= s.num ? '#3b82f6' : 'var(--card-bg)',
              border: step >= s.num ? 'none' : '2px solid var(--border-color)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 8px',
              color: step >= s.num ? 'white' : 'var(--text-secondary)'
            }}>
              {step > s.num ? <Check size={20} /> : s.num}
            </div>
            <div style={{ fontSize: '14px', color: step >= s.num ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
              {s.label}
            </div>
          </div>
        ))}
      </div>

      {/* Error */}
      {error && (
        <div style={{
          padding: '16px',
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '8px',
          marginBottom: '32px',
          display: 'flex',
          alignItems: 'center',
          color: '#ef4444'
        }}>
          <AlertCircle size={20} style={{ marginRight: '12px' }} />
          {error}
        </div>
      )}

      {/* Step 1: Create Account */}
      {step === 1 && (
        <div style={{ padding: '32px', background: 'var(--card-bg)', borderRadius: '12px' }}>
          <h2 style={{ fontSize: '24px', marginBottom: '24px' }}>Step 1: Create Cloudflare Account</h2>

          <div style={{ marginBottom: '24px' }}>
            <p style={{ marginBottom: '16px', lineHeight: '1.6' }}>
              If you don&apos;t have a Cloudflare account yet, create one for free:
            </p>

            <a
              href="https://dash.cloudflare.com/sign-up"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '12px 24px',
                background: '#3b82f6',
                color: 'white',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: 'bold'
              }}
            >
              Create Cloudflare Account
              <ExternalLink size={16} style={{ marginLeft: '8px' }} />
            </a>
          </div>

          <div style={{ padding: '16px', background: 'rgba(59, 130, 246, 0.1)', borderRadius: '8px', marginBottom: '24px' }}>
            <strong>Why Cloudflare?</strong>
            <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
              <li>Fastest deployment (~2 minutes)</li>
              <li>Global edge network (300+ cities)</li>
              <li>Workers AI: $0.01/1000 tokens</li>
              <li>D1 database: Free (25GB)</li>
              <li>R2 storage: $0.015/GB/month</li>
            </ul>
          </div>

          <button
            onClick={() => setStep(2)}
            style={{
              padding: '12px 32px',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            I have a Cloudflare account
          </button>
        </div>
      )}

      {/* Step 2: Get API Token */}
      {step === 2 && (
        <div style={{ padding: '32px', background: 'var(--card-bg)', borderRadius: '12px' }}>
          <h2 style={{ fontSize: '24px', marginBottom: '24px' }}>Step 2: Get API Token</h2>

          <div style={{ marginBottom: '24px' }}>
            <p style={{ marginBottom: '16px', lineHeight: '1.6' }}>
              Create an API token with the following permissions:
            </p>

            <div style={{ padding: '16px', background: 'rgba(59, 130, 246, 0.05)', borderRadius: '8px', marginBottom: '16px' }}>
              <strong>Required Permissions:</strong>
              <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                <li>Workers Scripts: Edit</li>
                <li>D1: Edit</li>
                <li>R2: Edit</li>
                <li>Workers AI: Read</li>
                <li>Workers KV: Edit</li>
              </ul>
            </div>

            <a
              href="https://dash.cloudflare.com/profile/api-tokens"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '12px 24px',
                background: '#3b82f6',
                color: 'white',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: 'bold'
              }}
            >
              Create API Token
              <ExternalLink size={16} style={{ marginLeft: '8px' }} />
            </a>
          </div>

          <div style={{ display: 'flex', gap: '16px' }}>
            <button
              onClick={() => setStep(1)}
              style={{
                padding: '12px 32px',
                background: 'transparent',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                fontSize: '16px',
                cursor: 'pointer'
              }}
            >
              Back
            </button>
            <button
              onClick={() => setStep(3)}
              style={{
                padding: '12px 32px',
                background: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              I have my API token
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Enter Credentials */}
      {step === 3 && (
        <div style={{ padding: '32px', background: 'var(--card-bg)', borderRadius: '12px' }}>
          <h2 style={{ fontSize: '24px', marginBottom: '24px' }}>Step 3: Enter Credentials</h2>

          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
              API Token
            </label>
            <input
              type="password"
              value={credentials.api_token}
              onChange={(e) => setCredentials({ ...credentials, api_token: e.target.value })}
              placeholder="Enter your Cloudflare API token"
              style={{
                width: '100%',
                padding: '12px',
                background: 'var(--body-bg)',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                fontSize: '16px',
                color: 'var(--text-primary)'
              }}
            />
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
              Account ID
            </label>
            <input
              type="text"
              value={credentials.account_id}
              onChange={(e) => setCredentials({ ...credentials, account_id: e.target.value })}
              placeholder="Enter your Cloudflare account ID"
              style={{
                width: '100%',
                padding: '12px',
                background: 'var(--body-bg)',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                fontSize: '16px',
                color: 'var(--text-primary)'
              }}
            />
            <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginTop: '8px' }}>
              Find your Account ID in Cloudflare Dashboard → Workers & Pages
            </p>
          </div>

          <div style={{ display: 'flex', gap: '16px' }}>
            <button
              onClick={() => setStep(2)}
              style={{
                padding: '12px 32px',
                background: 'transparent',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                fontSize: '16px',
                cursor: 'pointer'
              }}
            >
              Back
            </button>
            <button
              onClick={() => { void handleConnect() }}
              disabled={!credentials.api_token || !credentials.account_id || validating}
              style={{
                padding: '12px 32px',
                background: credentials.api_token && credentials.account_id && !validating ? '#3b82f6' : '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: credentials.api_token && credentials.account_id && !validating ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              {validating && <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />}
              {validating ? 'Validating...' : 'Connect Account'}
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Select Resources */}
      {step === 4 && (
        <div style={{ padding: '32px', background: 'var(--card-bg)', borderRadius: '12px' }}>
          <h2 style={{ fontSize: '24px', marginBottom: '24px' }}>Step 4: Select Resources</h2>

          <p style={{ marginBottom: '24px', color: 'var(--text-secondary)' }}>
            Choose which resources to deploy to your Cloudflare account:
          </p>

          {[
            { key: 'compute', label: 'Workers (Compute)', desc: 'Serverless functions', price: '$5/mo + $0.50/M requests' },
            { key: 'database', label: 'D1 (Database)', desc: 'SQLite at the edge', price: 'Free (25GB)' },
            { key: 'storage', label: 'R2 (Storage)', desc: 'Object storage', price: '$0.015/GB/month' },
            { key: 'ai', label: 'Workers AI', desc: 'LLM inference', price: '$0.01/1000 tokens' },
            { key: 'cache', label: 'KV (Cache)', desc: 'Key-value store', price: 'Free tier available' }
          ].map((resource) => (
            <div
              key={resource.key}
              onClick={() => setResources({ ...resources, [resource.key]: !resources[resource.key as keyof ResourceSelection] })}
              style={{
                padding: '16px',
                background: resources[resource.key as keyof ResourceSelection] ? 'rgba(59, 130, 246, 0.1)' : 'var(--body-bg)',
                border: resources[resource.key as keyof ResourceSelection] ? '2px solid #3b82f6' : '1px solid var(--border-color)',
                borderRadius: '8px',
                marginBottom: '16px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <div>
                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{resource.label}</div>
                <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>{resource.desc}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>{resource.price}</div>
              </div>
              <div style={{
                width: '24px',
                height: '24px',
                borderRadius: '4px',
                background: resources[resource.key as keyof ResourceSelection] ? '#3b82f6' : 'transparent',
                border: resources[resource.key as keyof ResourceSelection] ? 'none' : '2px solid var(--border-color)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white'
              }}>
                {resources[resource.key as keyof ResourceSelection] && <Check size={16} />}
              </div>
            </div>
          ))}

          <button
            onClick={() => { void handleDeploy() }}
            disabled={deploying}
            style={{
              width: '100%',
              padding: '16px',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '18px',
              fontWeight: 'bold',
              cursor: deploying ? 'not-allowed' : 'pointer',
              opacity: deploying ? 0.6 : 1,
              marginTop: '24px'
            }}
          >
            {deploying ? 'Deploying...' : 'Deploy to Cloudflare'}
          </button>
        </div>
      )}

      {/* Step 5: Deploying */}
      {step === 5 && deploying && (
        <div style={{ padding: '32px', background: 'var(--card-bg)', borderRadius: '12px', textAlign: 'center' }}>
          <Loader size={48} style={{ animation: 'spin 1s linear infinite', margin: '0 auto 24px' }} />
          <h2 style={{ fontSize: '24px', marginBottom: '16px' }}>Deploying Resources...</h2>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '24px' }}>{deploymentMessage}</p>

          <div style={{ width: '100%', height: '8px', background: 'var(--body-bg)', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{
              width: `${deploymentProgress}%`,
              height: '100%',
              background: '#3b82f6',
              transition: 'width 0.3s'
            }} />
          </div>
          <div style={{ marginTop: '8px', fontSize: '14px', color: 'var(--text-secondary)' }}>
            {deploymentProgress}% complete
          </div>
        </div>
      )}

      {/* Step 5: Complete */}
      {step === 5 && !deploying && (
        <div style={{ padding: '32px', background: 'var(--card-bg)', borderRadius: '12px', textAlign: 'center' }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: 'rgba(16, 185, 129, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px'
          }}>
            <Check size={48} style={{ color: '#10b981' }} />
          </div>

          <h2 style={{ fontSize: '32px', marginBottom: '16px' }}>Deployment Complete!</h2>
          <p style={{ fontSize: '18px', color: 'var(--text-secondary)', marginBottom: '32px' }}>
            Your AI infrastructure is now running on Cloudflare
          </p>

          <button
            onClick={() => navigate('/aicloud/dashboard')}
            style={{
              padding: '16px 48px',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '18px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            Go to Dashboard
          </button>
        </div>
      )}
    </div>
  )
}
